import { useState, useEffect, useRef } from "react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ArrowLeft, MapPin, Eye, Clock, Calendar, Percent, Store, Monitor, Users, Share2, Zap, Image as ImageIcon, BarChart3 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { useCart } from "@/utils/cartContext";
import { useQuery } from "@tanstack/react-query";
import { allDeals } from "@/data/deals";
import Navigation from "@/components/Navigation";
import Footer from "@/components/Footer1";
import { formatPrice } from "@/lib/utils";
import { getMediaOwner } from "@/data/media-owners";
import ReactFC from "react-fusioncharts";
import FusionCharts from "fusioncharts";
import Column2D from "fusioncharts/fusioncharts.charts";
import Pie2D from "fusioncharts/fusioncharts.charts";
import FusionTheme from "fusioncharts/themes/fusioncharts.theme.fusion";
import * as am5 from "@amcharts/amcharts5";
import * as am5hierarchy from "@amcharts/amcharts5/hierarchy";
import am5themes_Animated from "@amcharts/amcharts5/themes/Animated";

// Initialize FusionCharts
ReactFC.fcRoot(FusionCharts, Column2D, Pie2D, FusionTheme);

// AmCharts Hierarchy Component for Audience Analysis
function AmChartsHierarchy() {
  const chartRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) return;

    let root = am5.Root.new(chartRef.current);
    root.setThemes([am5themes_Animated.new(root)]);

    let container = root.container.children.push(
      am5.Container.new(root, {
        width: am5.percent(100),
        height: am5.percent(100),
        layout: root.horizontalLayout
      })
    );

    let series = container.children.push(
      am5hierarchy.ForceDirected.new(root, {
        singleBranchOnly: false,
        downDepth: 2,
        topDepth: 1,
        initialDepth: 2,
        valueField: "value",
        categoryField: "name",
        childDataField: "children",
        idField: "name",
        linkWithField: "linkWith",
        manyBodyStrength: -25,
        centerStrength: 1.2,
        minRadius: 20,
        maxRadius: 60,
        nodePadding: 5
      })
    );

    let data = {
      name: "SEGMENT",
      value: 200,
      children: [
        {
          name: "INTERESTS",
          value: 80,
          children: [
            { name: "Young families", value: 25 },
            { name: "Auto lovers", value: 22 },
            { name: "Fast food workers", value: 18 },
            { name: "Leisure mothers", value: 20 },
            { name: "Pet owners", value: 17 },
            { name: "Sports enthusiasts", value: 23 },
            { name: "Entertainment seekers", value: 19 }
          ]
        },
        {
          name: "OCCUPATION",
          value: 70,
          children: [
            { name: "Students", value: 25 },
            { name: "White collar", value: 30 },
            { name: "Income Group", value: 22 }
          ]
        },
        {
          name: "ACTIONS",
          value: 60,
          children: [
            { name: "Shopping", value: 20 },
            { name: "Commuting", value: 25 },
            { name: "Entertainment", value: 18 }
          ]
        }
      ]
    };

    series.data.setAll([data]);
    series.set("selectedDataItem", series.dataItems[0]);
    series.appear(1000, 100);

    return () => {
      root.dispose();
    };
  }, []);

  return <div ref={chartRef} style={{ width: "100%", height: "300px" }}></div>;
}

export default function DealDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addToCart, isInCart } = useCart();
  const [selectedGalleryImage, setSelectedGalleryImage] = useState(0);

  // Find deal by ID
  const deal = allDeals.find(d => d.id === id);

  if (!deal) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation mode="header" />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-bold text-gray-900 mb-4">Deal Not Found</h1>
            <p className="text-gray-600 mb-4">The deal you're looking for doesn't exist.</p>
            <Button onClick={() => navigate('/explore')}>
              Back to Explore
            </Button>
          </div>
        </div>
        <Footer configData={[]} yourLogo={""} />
      </div>
    );
  }

  const handleAddToCart = () => {
    const cartItem = {
      id: deal.id,
      name: deal.title,
      location: deal.location,
      state: deal.state || "Multiple",
      price: deal.discountedPrice,
      impressions: deal.impressions,
      size: deal.specifications?.resolution || "Various",
      type: "Deal" as const,
      status: deal.isAvailable ? "available" as const : "sold-out" as const,
      image: deal.imageUrl,
      specifications: {
        resolution: deal.specifications?.resolution || "Various",
        duration: deal.specifications?.duration || "Various"
      }
    };
    addToCart(cartItem);
  };

  const handleVisitStore = () => {
    // Set back URL context based on current page
    const currentPath = window.location.pathname;
    sessionStorage.setItem('mediaOwnerBackUrl', currentPath);
    console.log('Deal detail: Setting back URL to', currentPath);
    navigate(`/store/${deal.mediaOwnerId}`);
  };

  // Gallery images for the deal
  const galleryImages = [
    deal.imageUrl,
    "https://images.unsplash.com/photo-1573804633927-bfcbcd909acd?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1519003722824-194d4455a60c?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1485686531765-ba63b07845a7?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1560472354-b33ff0c44a43?w=800&h=400&fit=crop",
    "https://images.unsplash.com/photo-1540979388789-6cee28a1cdc9?w=800&h=400&fit=crop"
  ];

  // Mock demographic data for charts
  const ageChartData = {
    chart: {
      caption: "Age Demographics",
      subCaption: "Target audience age distribution",
      xAxisName: "Age Groups",
      yAxisName: "Percentage (%)",
      theme: "fusion",
      paletteColors: "#10b981,#34d399,#6ee7b7,#a7f3d0,#d1fae5",
      showValues: "1",
      valueFontColor: "#374151",
      valueFontSize: "12",
      valueFontBold: "1"
    },
    data: [
      { label: "18-24", value: "28" },
      { label: "25-34", value: "35" },
      { label: "35-44", value: "22" },
      { label: "45-54", value: "10" }, 
      { label: "55+", value: "5" }
    ]
  };

  const genderChartData = {
    chart: {
      caption: "Gender Distribution",
      subCaption: "Target audience gender breakdown",
      theme: "fusion",
      paletteColors: "#10b981,#34d399",
      showPercentValues: "1",
      showValues: "1",
      valueFontColor: "#374151",
      valueFontSize: "12",
      valueFontBold: "1"
    },
    data: [
      { label: "Male", value: "52" },
      { label: "Female", value: "48" }
    ]
  };

  // Related deals (mock data)
  const relatedDeals = [
    {
      id: "deal-002",
      title: "Melbourne CBD Flash Sale",
      location: "Melbourne, VIC",
      originalPrice: 980000,
      discountedPrice: 686000,
      discountPercent: 30,
      image: "https://images.unsplash.com/photo-1541888946425-d81bb19240f5?w=400&h=300&fit=crop",
      isAvailable: true
    },
    {
      id: "deal-003", 
      title: "Brisbane Transport Hub",
      location: "Brisbane, QLD",
      originalPrice: 850000,
      discountedPrice: 680000,
      discountPercent: 20,
      image: "https://images.unsplash.com/photo-1449824913935-59a10b8d2000?w=400&h=300&fit=crop",
      isAvailable: true
    },
    {
      id: "deal-004",
      title: "Adelaide Premium Deal",
      location: "Adelaide, SA", 
      originalPrice: 600000,
      discountedPrice: 360000,
      discountPercent: 40,
      image: "https://images.unsplash.com/photo-1570125909232-eb263c188f7e?w=400&h=300&fit=crop",
      isAvailable: false
    }
  ];

  return (
    <>
      <Navigation mode="header" />
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back Button */}
          <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/explore')}
          className="mb-6"
        >
          <ArrowLeft className="mr-2 h-4 w-4" />
          Back
          </Button>

          {/* Header Section - Similar to Billboard Detail */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
            {/* Main Image */}
            <div className="aspect-video rounded-lg overflow-hidden">
              <img
                src={deal.imageUrl}
                alt={deal.title}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Deal Info & Actions */}
            <div className="space-y-6">
              <div>
                {/* Deal badges and status */}
                <div className="flex flex-wrap items-center gap-2 mb-4">
                  <Badge className="bg-red-500 text-white font-bold">
                    <Percent className="h-3 w-3 mr-1" />
                    {deal.discountPercentage}% OFF
                  </Badge>
                  <Badge variant="secondary" className="bg-blue-100 text-blue-700">
                    {deal.inventoryType}
                  </Badge>
                  <Badge className={deal.isAvailable ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}>
                    {deal.isAvailable ? "Available" : "Sold Out"}
                  </Badge>
                </div>

                <h1 className="text-3xl font-bold text-emerald-600 mb-3">{deal.title}</h1>
                <p className="text-gray-600 mb-4">{deal.description}</p>

                {/* Location and impressions */}
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div className="flex items-center text-gray-600">
                    <MapPin className="h-4 w-4 mr-2" />
                    <span className="text-sm">{deal.location}</span>
                  </div>
                  <div className="flex items-center text-gray-600">
                    <Eye className="h-4 w-4 mr-2" />
                    <span className="text-sm">{deal.impressions.toLocaleString()} views</span>
                  </div>
                </div>

                {/* Media Owner link */}
                <button 
                  onClick={handleVisitStore}
                  className="text-emerald-600 hover:text-emerald-700 font-medium hover:underline mb-6 flex items-center space-x-1"
                >
                  <Store className="h-4 w-4" />
                  <span>Visit {deal.mediaOwnerName} Store</span>
                </button>
              </div>

              {/* Pricing Section */}
              <div className="bg-white p-6 rounded-lg border">
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-sm text-gray-500 line-through">
                        Original: {formatPrice(deal.originalPrice)}
                      </p>
                      <p className="text-2xl font-bold text-emerald-600">
                        {formatPrice(deal.discountedPrice)}
                      </p>
                      <p className="text-sm text-emerald-600 font-medium">
                        You save {formatPrice(deal.originalPrice - deal.discountedPrice)}
                      </p>
                    </div>
                    <div className="text-right">
                      <Badge className="bg-red-100 text-red-700">
                        Expires in: {deal.validUntil}
                      </Badge>
                    </div>
                  </div>

                  {/* Action buttons */}
                  <div className="flex space-x-3">
                    <Button
                      onClick={handleAddToCart}
                      disabled={!deal.isAvailable || isInCart(deal.id)}
                      className="flex-1"
                      variant={isInCart(deal.id) ? "secondary" : "default"}
                    >
                      {isInCart(deal.id) ? "In Cart" : !deal.isAvailable ? 'Sold Out' : 'Add to Cart'}
                    </Button>
                    <Button variant="outline" size="icon">
                      <Share2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Tabbed Content - Similar to Billboard Detail */}
          <div className="bg-white rounded-lg shadow-sm">
            <Tabs defaultValue="location" className="w-full">
              <TabsList className="grid w-full grid-cols-5 bg-gray-50 rounded-lg p-1">
                <TabsTrigger value="location" className="data-[state=active]:bg-white">Location</TabsTrigger>
                <TabsTrigger value="specification" className="data-[state=active]:bg-white">Specification</TabsTrigger>
                <TabsTrigger value="audiences" className="data-[state=active]:bg-white">Audiences</TabsTrigger>
                <TabsTrigger value="gallery" className="data-[state=active]:bg-white">Gallery</TabsTrigger>
                <TabsTrigger value="configuration" className="data-[state=active]:bg-white">Configuration</TabsTrigger>
              </TabsList>

              {/* Location Tab */}
              <TabsContent value="location" className="p-6">
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div>
                    <h3 className="text-lg font-semibold text-emerald-600 mb-4">Location Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Address:</span>
                      <span className="font-medium">{deal.location}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Area Type:</span>
                      <span className="font-medium">Commercial District</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Traffic Volume:</span>
                      <span className="font-medium">High</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Visibility:</span>
                      <span className="font-medium">Excellent</span>
                    </div>
                  </div>

                  <h4 className="text-md font-semibold text-emerald-600 mt-6 mb-4">Media Owner</h4>
                  <div className="flex items-center space-x-3">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center">
                      <Store className="h-6 w-6 text-emerald-600" />
                    </div>
                    <div>
                      <button 
                        onClick={handleVisitStore}
                        className="font-medium text-emerald-600 hover:text-emerald-700 hover:underline"
                      >
                        {deal.mediaOwnerName}
                      </button>
                      <p className="text-sm text-gray-500">Premium Media Partner</p>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-emerald-600 mb-4">Site Map</h3>
                  <div className="bg-gray-100 rounded-lg h-64 flex items-center justify-center">
                    <div className="text-center text-gray-500">
                      <MapPin className="h-8 w-8 mx-auto mb-2" />
                      <p>Interactive map would be displayed here</p>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Specification Tab */}
            <TabsContent value="specification" className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-600 mb-4">Deal Specifications</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deal Type:</span>
                      <span className="font-medium">{deal.dealType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Original Price:</span>
                      <span className="font-medium">{formatPrice(deal.originalPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discounted Price:</span>
                      <span className="font-medium text-emerald-600">{formatPrice(deal.discountedPrice)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Discount Percentage:</span>
                      <span className="font-medium text-red-600">{deal.discountPercentage}%</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Valid Until:</span>
                      <span className="font-medium">{deal.validUntil}</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-emerald-600 mb-4">Display Specifications</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Screen Size:</span>
                      <span className="font-medium">{deal.specifications?.resolution || "Various"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Display Type:</span>
                      <span className="font-medium">{deal.inventoryType}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Duration:</span>
                      <span className="font-medium">{deal.specifications?.duration || "Various"}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Daily Impressions:</span>
                      <span className="font-medium">{deal.impressions.toLocaleString()}</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Audiences Tab */}
            <TabsContent value="audiences" className="p-6">
              <div className="space-y-8">
                <div>
                  <div className="flex items-center justify-between mb-6">
                    <h3 className="text-lg font-semibold text-emerald-600">Audience Analytics</h3>
                    <div className="flex items-center space-x-4 text-sm text-gray-600">
                      <div className="flex items-center space-x-1">
                        <BarChart3 className="h-4 w-4" />
                        <span>Average frequency: 2.3x per week</span>
                      </div>
                    </div>
                  </div>

                  <div className="bg-blue-50 rounded-lg p-1">
                    <Tabs defaultValue="demographic" className="w-full">
                      <TabsList className="grid w-full grid-cols-2 bg-blue-50 p-1">
                        <TabsTrigger value="demographic" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                          Demographic details
                        </TabsTrigger>
                        <TabsTrigger value="audience" className="data-[state=active]:bg-white data-[state=active]:shadow-sm">
                          Audience analysis
                        </TabsTrigger>
                      </TabsList>

                      <TabsContent value="demographic" className="p-6 bg-white rounded-lg mt-2">
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                          <div>
                            <h4 className="text-md font-semibold text-gray-900 mb-4">Age Demographics</h4>
                            <ReactFC
                              type="column2d"
                              width="100%"
                              height="250"
                              dataFormat="json"
                              dataSource={ageChartData}
                            />
                          </div>
                          <div>
                            <h4 className="text-md font-semibold text-gray-900 mb-4">Gender Distribution</h4>
                            <ReactFC
                              type="pie2d"
                              width="100%"
                              height="250"
                              dataFormat="json"
                              dataSource={genderChartData}
                            />
                          </div>
                        </div>
                      </TabsContent>

                      <TabsContent value="audience" className="p-6 bg-white rounded-lg mt-2">
                        <div>
                          <h4 className="text-md font-semibold text-gray-900 mb-4">Audience Segmentation Analysis</h4>
                          <AmChartsHierarchy />
                        </div>
                      </TabsContent>
                    </Tabs>
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Gallery Tab */}
            <TabsContent value="gallery" className="p-6">
              <div className="space-y-6">
                <h3 className="text-lg font-semibold text-emerald-600">Deal Gallery</h3>
                
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <div className="aspect-video rounded-lg overflow-hidden">
                    <img
                      src={galleryImages[selectedGalleryImage]}
                      alt="Deal gallery"
                      className="w-full h-full object-cover"
                    />
                  </div>
                  
                  <div className="grid grid-cols-3 gap-3">
                    {galleryImages.map((image, index) => (
                      <button
                        key={index}
                        onClick={() => setSelectedGalleryImage(index)}
                        className={`aspect-video rounded-lg overflow-hidden border-2 transition-colors ${
                          selectedGalleryImage === index
                            ? 'border-emerald-500 ring-2 ring-emerald-200'
                            : 'border-gray-200 hover:border-gray-300'
                        }`}
                      >
                        <img
                          src={image}
                          alt={`Gallery ${index + 1}`}
                          className="w-full h-full object-cover"
                        />
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            </TabsContent>

            {/* Configuration Tab */}
            <TabsContent value="configuration" className="p-6">
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                <div>
                  <h3 className="text-lg font-semibold text-emerald-600 mb-4">Deal Configuration</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Deal ID:</span>
                      <span className="font-medium">{deal.id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Campaign Type:</span>
                      <span className="font-medium">Digital Display</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Booking Window:</span>
                      <span className="font-medium">7 days advance</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Cancellation:</span>
                      <span className="font-medium">48 hours notice</span>
                    </div>
                  </div>
                </div>

                <div>
                  <h3 className="text-lg font-semibold text-emerald-600 mb-4">Operation Details</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Operating Hours:</span>
                      <span className="font-medium">24/7</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Lighting:</span>
                      <span className="font-medium">LED Backlit</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Weather Proof:</span>
                      <span className="font-medium">Yes</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Power Supply:</span>
                      <span className="font-medium">Grid Connected</span>
                    </div>
                  </div>
                </div>
              </div>
            </TabsContent>
            </Tabs>
          </div>

          {/* Related Deals Section */}
          <div className="mt-12">
            <h3 className="text-2xl font-bold text-emerald-600 mb-6">You may also like these deals</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedDeals.map((relatedDeal) => (
                <Card key={relatedDeal.id} className="overflow-hidden hover:shadow-lg transition-shadow">
                  <div className="relative">
                    <img
                      src={relatedDeal.image}
                      alt={relatedDeal.title}
                      className="w-full h-48 object-cover"
                    />
                    <Badge className="absolute top-3 left-3 bg-red-500 text-white">
                      {relatedDeal.discountPercent}% OFF
                    </Badge>
                    <Badge 
                      className={`absolute top-3 right-3 ${
                        relatedDeal.isAvailable ? 'bg-emerald-500' : 'bg-red-500'
                      } text-white`}
                    >
                      {relatedDeal.isAvailable ? 'Available' : 'Sold Out'}
                    </Badge>
                  </div>
                  <CardContent className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-2">{relatedDeal.title}</h4>
                    <p className="text-sm text-gray-600 mb-3">{relatedDeal.location}</p>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm text-gray-500 line-through">
                          {formatPrice(relatedDeal.originalPrice)}
                        </p>
                        <p className="text-lg font-bold text-emerald-600">
                          {formatPrice(relatedDeal.discountedPrice)}
                        </p>
                      </div>
                      <Button 
                        size="sm" 
                        onClick={() => navigate(`/deal/${relatedDeal.id}`)}
                        disabled={!relatedDeal.isAvailable}
                      >
                        {relatedDeal.isAvailable ? 'View Deal' : 'Sold Out'}
                      </Button>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          </div>
        </div>
      </div>
      <Footer configData={[]} yourLogo={""} />
    </>
  );
}